import React, { useEffect, useRef, useState } from "react";
import "./LandingPage.css";
import appIcon from "./assets/landing/app-icon.png";
import libraryImg from "./assets/landing/library.jpg";
import collectionImg from "./assets/landing/collection.jpg";
import socialImg from "./assets/landing/social.jpg";
import box3dImg from "./assets/landing/box3d.jpg";
import valueImg from "./assets/landing/value.jpg";
import personalizationImg from "./assets/landing/personalization.jpg";

export const APP_STORE_URL = "https://apps.apple.com/app/id1344002205";
const PRIVACY_URL =
  "https://raullermen.github.io/politicas_privacidade/gamez_politicas_privacidade";

function useInView() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return [ref, inView];
}

function Rise({ children, className = "", delay = 0, id }) {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      id={id}
      className={`rise ${inView ? "rise-in" : ""} ${className}`}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </div>
  );
}

function StoreButton({ compact }) {
  return (
    <a
      href={APP_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={`store-button ${compact ? "store-button--compact" : ""}`}
    >
      <svg viewBox="0 0 24 24" className="store-button-glyph" aria-hidden="true">
        <path
          fill="currentColor"
          d="M16.365 1.43c0 1.14-.415 2.06-1.246 2.98-.998 1.08-2.2 1.7-3.51 1.6-.06-1.1.41-2.24 1.24-3.05.83-.83 2.24-1.44 3.5-1.53.02.17.03.34.02.5ZM20.6 17.15c-.5 1.15-.74 1.66-1.38 2.68-.9 1.42-2.16 3.2-3.73 3.21-1.4.02-1.76-.92-3.66-.91-1.9.01-2.3.93-3.7.91-1.57-.02-2.76-1.62-3.66-3.03-2.52-3.9-2.78-8.47-1.23-10.9 1.1-1.74 2.84-2.76 4.47-2.76 1.66 0 2.7.93 4.08.93 1.33 0 2.13-.94 4.08-.94 1.45 0 2.99.8 4.08 2.17-3.59 1.97-3.01 7.1.35 8.64Z"
        />
      </svg>
      <span className="store-button-copy">
        <small>Download on the</small>
        App Store
      </span>
    </a>
  );
}

function ShowcaseShot({ src, alt }) {
  return (
    <div className="shot">
      <div className="shot-glow" aria-hidden="true" />
      <div className="shot-frame">
        <span className="shot-notch" aria-hidden="true" />
        <img src={src} alt={alt} loading="lazy" />
      </div>
    </div>
  );
}

const SMALL_CARDS = [
  {
    key: "library",
    icon: (
      <>
        <rect x="4" y="4" width="7" height="16" rx="2" />
        <rect x="13" y="4" width="7" height="16" rx="2" />
      </>
    ),
    title: "Library",
    text: "Every game you own or want, sorted by platform and status.",
  },
  {
    key: "collection",
    icon: (
      <>
        <rect x="3.5" y="8" width="17" height="12" rx="2" />
        <path d="M3.5 8 6 4h12l2.5 4" />
        <path d="M10 12.5h4" />
      </>
    ),
    title: "Collection",
    text: "Photograph your physical shelf, build 3D covers and track condition and value.",
  },
  {
    key: "social",
    icon: (
      <>
        <circle cx="9" cy="9" r="3.2" />
        <circle cx="16.5" cy="10" r="2.4" />
        <path d="M3.5 20c0-3.3 2.5-5.5 5.5-5.5s5.5 2.2 5.5 5.5" />
        <path d="M15 20c0-2.3-.9-4-2.4-5.1" />
      </>
    ),
    title: "Social",
    text: "Build a profile, follow collectors, share what you're playing.",
  },
  {
    key: "discover",
    icon: (
      <>
        <circle cx="12" cy="12" r="8.5" />
        <path d="m15 9-4.2 2.8L9 15l4.2-2.8L15 9Z" />
      </>
    ),
    title: "Discover",
    text: "Recommendations that actually match your taste in games.",
  },
];

const PRO_PERKS = [
  "No-limit library — track as many games as you want",
  "Physical collection with prices, photos and condition",
  "Full insights — your journey, in charts",
  "Unbranded sharing — clean cards for your friends",
];

export default function LandingPage() {
  return (
    <div className="landing">
      <div className="landing-backdrop" aria-hidden="true" />
      <div className="landing-veil" aria-hidden="true" />

      <header className="topbar">
        <div className="topbar-inner glass">
          <div className="brand">
            <img src={appIcon} alt="" className="brand-mark" />
            <span>GameZ</span>
          </div>
          <nav className="topnav">
            <a href="#tour">Tour</a>
            <a href="#social">Social</a>
            <a href="#pro">Pro</a>
          </nav>
          <StoreButton compact />
        </div>
      </header>

      <main>
        <section className="hero">
          <div className="hero-copy">
            <p className="kicker">The home for your entire game collection</p>
            <h1>
              Every game you play.
              <br />
              Every game you own.
            </h1>
            <p className="hero-lede">
              GameZ tracks your backlog, catalogs your physical shelf and
              turns it all into stats you'll actually want to look at.
            </p>
            <div className="hero-actions">
              <StoreButton />
              <span className="hero-actions-note">Free to start · iPhone</span>
            </div>
          </div>

          <div className="hero-visual">
            <ShowcaseShot src={libraryImg} alt="GameZ library screen" />
          </div>
        </section>

        <Rise className="feature-row">
          <div className="feature-row-inner glass">
            <div className="feature-copy">
              <span className="pill">New</span>
              <h2>Every box, in 3D.</h2>
              <p>
                Your games appear as real boxes — spin them to see front and
                back. Official box art, or covers you build from your own
                photos.
              </p>
            </div>
            <ShowcaseShot src={box3dImg} alt="GameZ 3D box cover picker" />
          </div>
        </Rise>

        <Rise className="feature-row feature-row--reverse">
          <div className="feature-row-inner glass">
            <ShowcaseShot src={collectionImg} alt="GameZ physical collection screen, boxart view" />
            <div className="feature-copy">
              <p className="kicker">Physical collection</p>
              <h2>Your shelf, fully catalogued.</h2>
              <p>
                Every cartridge, disc or box — with real box art, 3D covers,
                condition and price paid, so your shelf's value adds up on
                its own.
              </p>
            </div>
          </div>
        </Rise>

        <Rise className="feature-row feature-row--reverse">
          <div className="feature-row-inner glass">
            <ShowcaseShot src={valueImg} alt="GameZ collection value screen" />
            <div className="feature-copy">
              <h2>Know what your collection is worth.</h2>
              <p>
                Market prices for your physical games, tracked automatically.
                Watch your shelf's value grow.
              </p>
            </div>
          </div>
        </Rise>

        <Rise className="section-head" id="tour">
          <p className="kicker kicker--center">Everything, at a glance</p>
          <h2>One shelf. Every platform, every status.</h2>
        </Rise>

        <Rise className="bento">
          <div className="bento-card bento-card--wide glass">
            <div className="bento-icon">
              <svg viewBox="0 0 24 24" className="bento-icon-svg" aria-hidden="true">
                <rect x="3.5" y="12" width="4" height="8" rx="1" />
                <rect x="10" y="7" width="4" height="13" rx="1" />
                <rect x="16.5" y="4" width="4" height="16" rx="1" />
              </svg>
            </div>
            <h3>Insights</h3>
            <p>Completion rate, backlog funnel and ownership breakdown.</p>
            <div className="insight-stats">
              <div className="insight-stat">
                <strong>31%</strong>
                <span>Completion rate</span>
              </div>
              <div className="insight-stat">
                <strong>4.7</strong>
                <span>Avg. rating</span>
              </div>
              <div className="insight-stat">
                <strong>57</strong>
                <span>Owned copies</span>
              </div>
            </div>
            <div className="funnel" role="img" aria-label="Backlog progress funnel">
              <span className="funnel-seg" style={{ flex: 12, background: "var(--c-wishlist)" }} />
              <span className="funnel-seg" style={{ flex: 7, background: "var(--c-playing)" }} />
              <span className="funnel-seg" style={{ flex: 15, background: "var(--c-finished)" }} />
              <span className="funnel-seg" style={{ flex: 41, background: "var(--c-completed)" }} />
            </div>
            <div className="funnel-legend">
              <span><i style={{ background: "var(--c-wishlist)" }} />Wishlist 12</span>
              <span><i style={{ background: "var(--c-playing)" }} />Playing 7</span>
              <span><i style={{ background: "var(--c-finished)" }} />Finished 15</span>
              <span><i style={{ background: "var(--c-completed)" }} />Completed 41</span>
            </div>
          </div>

          {SMALL_CARDS.map((c) => (
            <div className={`bento-card bento-card--${c.key} glass`} key={c.key}>
              <div className="bento-icon">
                <svg
                  viewBox="0 0 24 24"
                  className="bento-icon-svg"
                  aria-hidden="true"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  {c.icon}
                </svg>
              </div>
              <h3>{c.title}</h3>
              <p>{c.text}</p>
            </div>
          ))}
        </Rise>

        <Rise className="feature-row feature-row--reverse">
          <div className="feature-row-inner glass">
            <ShowcaseShot src={personalizationImg} alt="GameZ customize theme screen" />
            <div className="feature-copy">
              <p className="kicker">Themes</p>
              <h2>Make it yours.</h2>
              <p>
                Custom themes, colors and backgrounds — your collection, your
                look.
              </p>
            </div>
          </div>
        </Rise>

        <Rise className="feature-row" id="social">
          <div className="feature-row-inner glass">
            <div className="feature-copy">
              <span className="pill">New · Beta</span>
              <h2>Share your shelf with friends.</h2>
              <p>
                A profile for your library, a showcase for your favorites and
                a feed of what you're playing right now. GameZ Social is
                rolling out today.
              </p>
            </div>
            <ShowcaseShot src={socialImg} alt="GameZ social profile screen" />
          </div>
        </Rise>

        <Rise className="pro-section" id="pro">
          <div className="pro-panel glass">
            <div className="pro-copy">
              <p className="kicker">GameZ Pro</p>
              <h2>Unlock everything in GameZ.</h2>
              <ul className="pro-list">
                {PRO_PERKS.map((perk) => (
                  <li key={perk}>{perk}</li>
                ))}
              </ul>
              <div className="pro-plans">
                <div className="pro-plan pro-plan--best">
                  <span className="pro-plan-tag">Save 58%</span>
                  <span className="pro-plan-name">Annual</span>
                  <span className="pro-plan-price">$14.99</span>
                  <span className="pro-plan-period">/ year</span>
                </div>
                <div className="pro-plan">
                  <span className="pro-plan-name">Monthly</span>
                  <span className="pro-plan-price">$2.99</span>
                  <span className="pro-plan-period">/ month</span>
                </div>
                <div className="pro-plan">
                  <span className="pro-plan-name">Lifetime</span>
                  <span className="pro-plan-price">$39.99</span>
                  <span className="pro-plan-period">one time</span>
                </div>
              </div>
            </div>
          </div>
        </Rise>

        <Rise className="final-cta">
          <h2>Start tracking your games today.</h2>
          <StoreButton />
        </Rise>
      </main>

      <footer className="footer">
        <div className="brand">
          <img src={appIcon} alt="" className="brand-mark" />
          <span>GameZ</span>
        </div>
        <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
          Privacy Policy
        </a>
        <p>&copy; {new Date().getFullYear()} GameZ</p>
      </footer>
    </div>
  );
}
